import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboard as dashboardApi } from '@/api/dashboard';
import { analyses as analysesApi } from '@/api/analyses';
import { workItems as workItemsApi } from '@/api/workItems';
import type { WorkItemPullRequest, WorkItemView } from '@/api/types';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import EmptyState from '@/components/EmptyState';
import { useConfirm } from '@/components/ConfirmDialog';
import { actualEffortTone, actualVsNoAi, effortToneAccent } from '@/lib/effortTone';
import { formatDate, formatHours, trim } from '@/lib/format';
import { formatEstimate, formatBand } from '@/lib/estimate';
import { useEstimateMode } from '@/estimate/EstimateModeContext';

type Scope = 'jira' | 'pr';

function repoDisplayName(fullName: string): string {
  const idx = fullName.indexOf('/');
  return idx === -1 ? fullName : fullName.slice(idx + 1);
}

export default function WorkItemDetailPage() {
  const { id: orgId, org } = useRequireOrg();
  const canDelete = org.role === 'OWNER' || org.role === 'ADMIN';
  const qc = useQueryClient();
  const { mode } = useEstimateMode();
  const confirm = useConfirm();
  const { scope, id, author } = useParams<{ scope: Scope; id: string; author: string }>();

  // Fetch all-time work items for this org (no filters). The list is bounded by org volume
  // and react-query caches across page transitions, so this is cheap for typical tenants.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'work-items', orgId, 'all-time'],
    queryFn: () => dashboardApi.workItems(orgId, {}),
  });

  const invalidateActuals = () => {
    qc.invalidateQueries({ queryKey: ['dashboard', 'work-items', orgId] });
    qc.invalidateQueries({ queryKey: ['analyses-work-items', orgId] });
    qc.invalidateQueries({ queryKey: ['dashboard', orgId] });
  };

  const removePrMutation = useMutation({
    mutationFn: (prId: string) => analysesApi.remove(orgId, prId),
    onSuccess: () => {
      invalidateActuals();
      qc.invalidateQueries({ queryKey: ['analyses', orgId] });
    },
  });

  const setActualMutation = useMutation({
    mutationFn: (hours: number | null) => {
      if (!scope || !id || !author) throw new Error('missing route params');
      return workItemsApi.setActualHours(orgId, scope, id, author, hours);
    },
    onSuccess: () => invalidateActuals(),
  });

  const handleDeletePr = async (prId: string, prNumber: number) => {
    const ok = await confirm({
      title: 'Delete analysis',
      message: `Delete analysis for PR #${prNumber} from history?\n\nThe analysis will be hidden from lists and metrics. Credits will NOT be refunded.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) removePrMutation.mutate(prId);
  };

  const item: WorkItemView | undefined = useMemo(() => {
    if (!data || !scope || !id || !author) return undefined;
    const targetKey = scope === 'jira' ? `jira:${id}` : `pr:${id}`;
    return data.find(w => w.workItemKey === targetKey && w.author === author);
  }, [data, scope, id, author]);

  const heading = item
    ? item.issueKey
      ? `${item.issueKey}${item.issueSummary ? ` — ${item.issueSummary}` : ''}`
      : `PR #${item.pullRequests[0]?.prNumber ?? '?'} (standalone)`
    : 'Work item';

  const subtitleParts: React.ReactNode[] = [];
  if (item) {
    if (item.issueType) subtitleParts.push(<span key="t">{item.issueType}</span>);
    if (item.issueStatus) subtitleParts.push(<span key="s">{item.issueStatus}</span>);
    subtitleParts.push(<span key="a">@{item.author}</span>);
    subtitleParts.push(
      <span key="c">
        {item.pullRequestCount} pull request{item.pullRequestCount === 1 ? '' : 's'}
      </span>,
    );
  }

  return (
    <>
      <PageHeader
        title={heading}
        subtitle={
          subtitleParts.length > 0 ? (
            <span style={{ display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {subtitleParts.flatMap((node, idx) =>
                idx === 0 ? [node] : [<span key={`d${idx}`} className="muted">·</span>, node],
              )}
            </span>
          ) : null
        }
      />

      <p>
        <Link to="/analyses" className="link">
          ← Back to analyses
        </Link>
      </p>

      {isLoading ? <Spinner label="Loading work item…" /> : null}
      {error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}
      {removePrMutation.error ? <ErrorBanner error={removePrMutation.error} /> : null}
      {setActualMutation.error ? <ErrorBanner error={setActualMutation.error} /> : null}

      {data && !item ? (
        <EmptyState
          title="Work item not found"
          description="It may have been removed, or it falls outside any analysis we've seen."
        />
      ) : null}

      {item ? (
        <>
          <section className="card">
            <h3>Aggregated effort</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              Estimates sum across the {item.pullRequestCount} PR{item.pullRequestCount === 1 ? '' : 's'} below.
              Actual hours are tracked once at the issue level (Jira or manual) — never double-counted across PRs.
            </p>
            <div className="metric-grid">
              <div className={`metric ${actualMetricAccentClass(item)}`}>
                <div className="metric-label">Actual hours</div>
                <div className="metric-value">
                  <ActualHoursEditor
                    item={item}
                    isSaving={setActualMutation.isPending}
                    onSave={(hours) => setActualMutation.mutate(hours)}
                  />
                </div>
              </div>
              <Metric
                label="AI-assisted estimate"
                value={formatEstimate(item.estimatedHoursMin, item.estimatedHoursLikely, item.estimatedHoursMax, mode)}
                hint={`Band ${formatBand(item.estimatedHoursMin, item.estimatedHoursMax)}`}
              />
              <Metric
                label="No-AI estimate"
                value={formatEstimate(item.unassistedHoursMin, item.unassistedHoursLikely, item.unassistedHoursMax, mode)}
                hint={`Band ${formatBand(item.unassistedHoursMin, item.unassistedHoursMax)}`}
              />
              {(() => {
                const cmp = actualVsNoAi(
                  item.pullRequestsWithActual > 0 ? item.actualHoursTotal : null,
                  item.unassistedHoursMin,
                  item.unassistedHoursMax,
                  trim,
                );
                return (
                  <Metric
                    label="vs no-AI estimate"
                    value={cmp.label}
                    accent={cmp.accent}
                    hint={cmp.hint}
                  />
                );
              })()}
            </div>
          </section>

          <section className="card">
            <h3>What was done</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              {item.pullRequestCount === 1
                ? 'The analysis for the pull request that delivered this work item.'
                : `Combined work across ${item.pullRequestCount} pull requests. Each block is one PR's analysis — expand for factors, risks, and the per-stage breakdown.`}
            </p>
            <div className="pr-work-list">
              {item.pullRequests.map(pr => (
                <PrWorkCard
                  key={pr.id}
                  pr={pr}
                  canDelete={canDelete}
                  deleting={removePrMutation.isPending}
                  onDelete={() => handleDeletePr(pr.id, pr.prNumber)}
                  singlePr={item.pullRequestCount === 1}
                />
              ))}
            </div>
          </section>

          {item.issueKey === null ? (
            <section className="card">
              <p className="muted small" style={{ margin: 0 }}>
                This work item is a standalone pull request — no Jira issue was linked at the
                time of analysis. To group it with others, link it to an issue on the PR detail
                page and pick it as the primary issue.
              </p>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/**
 * One pull request rendered inline as part of the work item — title, the badges that
 * characterize it, its own estimate, the plain-English summary, and an expandable block
 * with the deeper analysis. Lets a manager read everything that went into the work item on
 * a single screen instead of opening each PR.
 */
function PrWorkCard({
  pr,
  canDelete,
  deleting,
  onDelete,
  singlePr,
}: {
  pr: WorkItemPullRequest;
  canDelete: boolean;
  deleting: boolean;
  onDelete: () => void;
  singlePr: boolean;
}) {
  const { mode } = useEstimateMode();
  const hasDetails = !!(pr.mainFactors || pr.risks || pr.workBreakdown);
  return (
    <article className="pr-work-card">
      <div className="pr-work-head">
        <div className="pr-work-title">
          <a href={pr.prUrl} target="_blank" rel="noreferrer" className="link">
            #{pr.prNumber} {pr.prTitle} ↗
          </a>
          <div className="muted small">
            {repoDisplayName(pr.repositoryFullName)}
            {pr.prMergedAt ? <> · merged {formatDate(pr.prMergedAt)}</> : null}
          </div>
        </div>
        <div className="pr-work-actions">
          <Link to={`/analyses/${pr.id}`} className="link small" title="Full analysis & PR metrics">
            Details →
          </Link>
          {canDelete ? (
            <button
              type="button"
              className="btn btn-ghost btn-danger"
              aria-label={`Delete analysis for PR #${pr.prNumber}`}
              title="Delete from history"
              disabled={deleting}
              onClick={onDelete}
            >
              🗑
            </button>
          ) : null}
        </div>
      </div>

      <div className="pr-work-badges">
        {pr.complexity ? <span className="badge badge-muted">Complexity: {pr.complexity}</span> : null}
        {pr.aiAssistanceSuitability ? (
          <span className="badge badge-info">AI fit: {pr.aiAssistanceSuitability}</span>
        ) : null}
        {pr.confidence ? <span className="badge badge-muted">Confidence: {pr.confidence}</span> : null}
        <span className="badge badge-muted" title={`Band ${formatBand(pr.estimatedHoursMin, pr.estimatedHoursMax)}`}>
          AI {formatEstimate(pr.estimatedHoursMin, pr.estimatedHoursLikely, pr.estimatedHoursMax, mode)}
        </span>
        <span className="badge badge-muted" title={`Band ${formatBand(pr.unassistedHoursMin, pr.unassistedHoursMax)}`}>
          No-AI {formatEstimate(pr.unassistedHoursMin, pr.unassistedHoursLikely, pr.unassistedHoursMax, mode)}
        </span>
      </div>

      {pr.summary ? (
        <p className="pr-work-summary">{pr.summary}</p>
      ) : (
        <p className="muted small">No summary recorded for this PR.</p>
      )}

      {hasDetails ? (
        <details className="pr-work-details">
          <summary>{singlePr ? 'Factors, risks & breakdown' : 'More detail'}</summary>
          {pr.mainFactors ? (
            <div className="pr-work-detail-block">
              <h4>Main factors</h4>
              <pre className="multiline">{pr.mainFactors}</pre>
            </div>
          ) : null}
          {pr.risks ? (
            <div className="pr-work-detail-block">
              <h4>Risks</h4>
              <pre className="multiline">{pr.risks}</pre>
            </div>
          ) : null}
          {pr.workBreakdown ? (
            <div className="pr-work-detail-block">
              <h4>Work breakdown</h4>
              <pre className="multiline">{pr.workBreakdown}</pre>
            </div>
          ) : null}
        </details>
      ) : null}
    </article>
  );
}

function actualMetricAccentClass(item: WorkItemView): string {
  const tone = actualEffortTone(
    item.pullRequestsWithActual > 0 ? item.actualHoursTotal : null,
    item.estimatedHoursMax,
    item.unassistedHoursMax,
  );
  const accent = effortToneAccent(tone);
  return accent ? `metric-${accent}` : '';
}

/**
 * Inline editor for a work item's actual hours. Mirrors the click-to-edit pattern from
 * the legacy ActualHoursCell so the UX feels familiar — click the value to edit, Enter
 * to commit, Esc to cancel, empty value clears the manual override.
 */
function ActualHoursEditor({
  item,
  isSaving,
  onSave,
}: {
  item: WorkItemView;
  isSaving: boolean;
  onSave: (hours: number | null) => void;
}) {
  const current = item.pullRequestsWithActual > 0 ? item.actualHoursTotal : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(current == null ? '' : String(current));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(current == null ? '' : String(current));
  }, [current]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      if (current != null) onSave(null);
      setEditing(false);
      return;
    }
    const parsed = Number(trimmed.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      setDraft(current == null ? '' : String(current));
      setEditing(false);
      return;
    }
    if (current != null && Math.abs(parsed - current) < 0.001) {
      setEditing(false);
      return;
    }
    onSave(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="actual-hours-edit">
        <input
          ref={inputRef}
          className="input"
          type="number"
          inputMode="decimal"
          min={0}
          step={0.05}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(current == null ? '' : String(current));
              setEditing(false);
            }
          }}
          aria-label="Actual hours"
        />
        <span className="muted small">h</span>
        {isSaving ? <span className="muted small">…</span> : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="actual-hours-cell"
      onClick={() => setEditing(true)}
      title={
        item.manualActualOverride
          ? 'Manual override — Jira sync no longer touches this value. Click to edit; clear the field to reset.'
          : 'Tracked from Jira. Click to override manually. Clear the field to revert to the Jira-synced value.'
      }
    >
      {current == null ? (
        <span className="muted small">Click to enter hours</span>
      ) : (
        <span>{formatHours(current)}</span>
      )}
      {item.manualActualOverride ? (
        <span className="muted small">(manual)</span>
      ) : null}
      <span className="edit-icon" aria-hidden>✎</span>
    </button>
  );
}

function Metric({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: 'good' | 'warn' | 'amber';
  hint?: string;
}) {
  return (
    <div className={`metric ${accent ? `metric-${accent}` : ''}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {hint ? <div className="muted small" style={{ marginTop: '0.25rem' }}>{hint}</div> : null}
    </div>
  );
}
